// Helpers for preserving the page a signed-out user was trying to reach
// across the /sign-in → /init → final page round-trip.

// Reject protocol-relative ("//evil.com") and absolute URLs so a crafted
// redirect_url can't bounce users off our origin.
export const isSafeRelativePath = (value) =>
  typeof value === 'string' && value.startsWith('/') && !value.startsWith('//');

const AUTH_RETURN_PATHS = new Set(['/sign-in', '/sign-up', '/magic-link', '/init']);

// The paths whose page IS an auth flow, as opposed to /init which is where a
// finished one lands. Clerk (and MagicLink) drive multi-step state on these —
// SSO callback, second factor, session tasks — and own the redirect out of
// them, so the app must keep them mounted across the signed-out → signed-in
// transition instead of swapping trees underneath the flow. See the
// short-circuit in modules/App/_app.jsx.
const AUTH_FLOW_PATHS = new Set(['/sign-in', '/sign-up', '/magic-link']);

export const isAuthFlowPath = (pathname) => AUTH_FLOW_PATHS.has(pathname);

// Read and validate ?redirect_url= from the current URL (or a provided
// search string). Returns the path or null.
export const getSafeRedirectUrl = (search = window.location.search) => {
  const raw = new URLSearchParams(search).get('redirect_url');
  return isSafeRelativePath(raw) ? raw : null;
};

// Clerk hands the browser its session state through URL parameters — the
// handshake token, the dev-browser JWT, the satellite sync marker — and
// clerk-js strips them only once it has consumed them. Anything that redirects
// while they are still on the URL destroys the hand-off, because
// buildSignInUrlWithRedirect below collapses everything to a bare path. That is
// what turns a one-off SSO landing failure into a permanent /sign-in loop: the
// user arrives holding the state that would have signed them in, and we throw
// it away before Clerk can read it.
//
// Matching is on parameter NAMES, never on the raw URL. A `redirect_url` value
// can legitimately contain the string "__clerk" (we encode a whole path into
// it), and a substring test on the href would then suppress the redirect
// forever.
const isClerkParamName = (name) => name.startsWith('__clerk') || name === '__session';

const paramsCarryClerkState = (search) => {
  if (!search) return false;
  for (const name of new URLSearchParams(search).keys()) {
    if (isClerkParamName(name)) return true;
  }
  return false;
};

// Clerk also delivers state in the fragment: handshake params as
// `#__clerk_db_jwt=...`, and its own virtual routing as `#/sso-callback`,
// `#/factor-one`, and so on. The app itself mounts BrowserRouter, never
// HashRouter, so a `#/...` fragment is never one of our routes — it belongs to
// Clerk and must survive.
const hashCarriesClerkState = (hash) => {
  if (!hash) return false;
  const raw = hash.startsWith('#') ? hash.slice(1) : hash;
  if (!raw) return false;
  if (raw.startsWith('/')) return true;
  return paramsCarryClerkState(raw);
};

// True while Clerk state is still sitting unconsumed on the URL. Callers should
// hold off redirecting — but only for a bounded grace period, since nothing
// guarantees clerk-js will ever clear it (see ClerkHandshakeGate in _app.jsx).
export const hasPendingClerkHandshake = (location = window.location) =>
  paramsCarryClerkState(location?.search) || hashCarriesClerkState(location?.hash);

// Build the /sign-in URL for a signed-out visitor, preserving where they
// were heading. `location` is a react-router location object.
export const buildSignInUrlWithRedirect = (location) => {
  // The hash is part of "where they were heading" too — dropping it silently
  // strips deep links back to a specific tab/anchor after sign-in.
  const fullPath = (location.pathname || '') + (location.search || '') + (location.hash || '');
  const skip =
    !fullPath ||
    fullPath === '/' ||
    fullPath.startsWith('/sign-in') ||
    fullPath.startsWith('/sign-up') ||
    fullPath.startsWith('/magic-link');
  if (skip) return '/sign-in';
  return `/sign-in?redirect_url=${encodeURIComponent(fullPath)}`;
};

// Append the (already-validated) redirect target to a base URL so it
// survives the trip through /init.
export const appendRedirectUrl = (baseUrl, redirectUrl) => {
  if (!redirectUrl) return baseUrl;
  const sep = baseUrl.includes('?') ? '&' : '?';
  return `${baseUrl}${sep}redirect_url=${encodeURIComponent(redirectUrl)}`;
};

export const authReturnTargets = (pathname, search, destinationMatches) =>
  AUTH_RETURN_PATHS.has(pathname) && destinationMatches(getSafeRedirectUrl(search));
